import type { CurrentUser, UserTenant } from "../hooks/useAuthRestApi";

/**
 * テナントロール型（バックエンドのTenantRole enumと同期）
 * @see backend/services/ses-search-api/src/ses_search_api/types/enums.py
 */
export type TenantRole = "owner" | "admin" | "member";

/**
 * テナントロール定数
 */
export const TENANT_ROLES = {
  OWNER: "owner",
  ADMIN: "admin",
  MEMBER: "member",
} as const satisfies Record<Uppercase<TenantRole>, TenantRole>;

/**
 * 管理者ロール（owner/admin）
 */
const ADMIN_ROLES: readonly TenantRole[] = [
  TENANT_ROLES.OWNER,
  TENANT_ROLES.ADMIN,
];

/**
 * 引数のorganizationIdが開発環境用の表示を許可されたアカウントかどうか
 * @param {string | null}  organizationId - 組織ID
 * @return {boolean} 開発用かどうか
 */
export const isAllowDevelopUse = (organizationId: string | null): boolean => {
  // ローカル開発環境では常に許可
  if (window.location.hostname === "localhost") {
    return true;
  }

  if (!organizationId) {
    return false;
  }
  return [
    "5cbf2941-be8b-4866-a6a9-9cac0b2ac723", // Tech Reis Dev (legacy organizationId)
    "323d67dc-1fb7-4931-97fc-263bdb48cf4b", // Tech Reis Prd (legacy organizationId)
    "e07d14f3-856d-4e65-8b12-542703d9e718", // Dev tenant (new tenantId)
  ].includes(organizationId);
};

/**
 * ユーザーがテナント管理者（owner/admin）かどうかを判定
 * @param {CurrentUser | null} user - 現在のユーザー情報
 * @return {boolean} テナント管理者の場合true
 */
export const isTenantAdmin = (user: CurrentUser | null): boolean => {
  if (!user?.tenants || user.tenants.length === 0) {
    return false;
  }
  // 最初のテナントのロールをチェック（複数テナント対応時は変更が必要）
  const tenant = user.tenants[0];
  return ADMIN_ROLES.includes(tenant.role as TenantRole);
};

/**
 * ユーザーの現在のテナントを取得
 * @param {CurrentUser | null} user - 現在のユーザー情報
 * @return {UserTenant | null} テナント情報
 */
export const getCurrentTenant = (
  user: CurrentUser | null,
): UserTenant | null => {
  if (!user?.tenants || user.tenants.length === 0) {
    return null;
  }
  return user.tenants[0];
};
